import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { handlesOwnErrors } from 'app/helpers/http-context';

import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class StorageService {

    /**
     *
     * @param _http
     */
    constructor(private _http: HttpClient) { }

    /**
     * Guarda un objeto serializado como JSON en el storage
     * @param container Nombre base del contenedor (el backend le agrega el ambiente)
     * @param key Llave con la que se guarda el objeto
     * @param value Objeto a guardar
     * @returns La llave con la que quedó guardado el objeto
     */
    set<T>(container: string, key: string, value: T): Observable<string> {
        return this._http.put(`${environment.repositoriesApiUrl}/api/storage`, value, {
            params: this.params(container, key)
        }).pipe(
            map((response: any) => response.key as string),
            catchError(e => {
                return throwError(e);
            })
        );
    }

    /**
     * Obtiene un objeto guardado como JSON desde el storage
     *
     * Que la llave no exista es parte del flujo normal, no un error: la
     * petición se marca para que el interceptor no avise al usuario y para
     * recibir la respuesta original, que es la única forma de distinguir el
     * 404 de una caída del servicio.
     *
     * @param container Nombre base del contenedor (el backend le agrega el ambiente)
     * @param key Llave con la que se guardó el objeto
     * @returns El objeto guardado, o null si la llave no existe
     */
    get<T>(container: string, key: string): Observable<T> {
        return this._http.get<T>(`${environment.repositoriesApiUrl}/api/storage`, {
            params: this.params(container, key),
            context: handlesOwnErrors()
        }).pipe(
            catchError((e: HttpErrorResponse) => {
                // La llave no existe o el contenido no se pudo leer
                if (e?.status === 404) {
                    return of(null as T);
                }

                return throwError(() => e);
            })
        );
    }

    /**
     * Arma los parámetros que espera el controlador de storage
     * @param container Nombre base del contenedor
     * @param key Llave del objeto
     * @returns Los parámetros de la petición
     */
    private params(container: string, key: string): HttpParams {
        return new HttpParams()
            .set('container', container)
            .set('key', key);
    }
}
